import { ConfigRecord } from "./ConfigRecord";
import * as GlobalTypes from "../../types";

export type ChemistryRecord = ConfigRecord<ChemistryConfig>;
export type ChemistryConfig = BowChemistry[];

export class BowChemistry {
    AcidDosingProcedure: ChemicalDosingProcedure;
    AcidDosingProcedureUseDefault: boolean;
    BaseDosingProcedure: ChemicalDosingProcedure;
    BaseDosingProcedureUseDefault: boolean;
    BodyOfWaterUUID: GlobalTypes.UUIDV4;
    CO2DosingProcedure: ChemicalDosingProcedure;
    CO2DosingProcedureUseDefault: boolean;
    DispensingAcidEnabled: boolean = false;
    DispensingBaseEnabled: boolean;
    DispensingCO2Enabled: boolean;
    DispensingLiquidChlorineEnabled: boolean = false;
    DispensingSaltChlorineEnabled: boolean = false;
    LiquidChlorinatorDispenseMethod: GlobalTypes.LiquidChlorinatorDispenseMethods = "AUTOMATIC_PROBE";
    LiquidChlorineDosingProcedure: ChemicalDosingProcedure;
    LiquidChlorineDosingProcedureUseDefault: boolean;
    SaltChlorineDosing: SaltDosingProcedure;
    MaxSaltChlorinatorPPMDosePerDay: number = 1;
    /** @deprecated Removed by AutoFilterVersion 32 */
    ORPAlertDelayMinutes: number = 60;
    /** @deprecated Removed by AutoFilterVersion 32 */
    ORPAlertEnabled: boolean = true;
    /** @deprecated Removed by AutoFilterVersion 32 */
    ORPAlertTooHigh: number;
    /** @deprecated Removed by AutoFilterVersion 32 */
    ORPAlertTooLow: number;
    /** @deprecated Removed by AutoFilterVersion 32 */
    ORPTarget: number = 650;
    OzoneEnabled: boolean;
    PHAlertDelayMinutes: number = 60;
    PHAlertEnabled: boolean = true;
    PHAlertTooHigh: number;
    PHAlertTooLow: number;
    PHTarget:number = 7.6;
    /** @deprecated Use SaltChlorineDosing.NotCovered.SaltChlorinatorDispenseMethod or SaltChlorineDosing.Covered.SaltChlorinatorDispenseMethod instead. */
    SaltChlorinatorDispenseMethod: GlobalTypes.SaltChlorineDispenseMethods = GlobalTypes.SaltChlorineDispenseMethods.AUTOMATIC_PROBE;
    SanitizerAlertDelayMinutes: number;
    SanitizerAlertEnabled: boolean;
    SanitizerAlertTooHigh: SanitizerTarget;
    SanitizerAlertTooLow: SanitizerTarget;
    SanitizerTarget: SanitizerTarget;
    StandAloneSaltChlorinatorPercent: number =  50;
    UvSanitizerEnabled: boolean;
} 

export class SanitizerTarget {
    Target: number;
    Type: SanitizerTypes;
}

export enum SanitizerTypes {
    FREE_CHLORINE = "FREE_CHLORINE",
    ORP = "ORP",
    TOTAL_CHLORINE = "TOTAL_CHLORINE",
}

export enum ChemicalDosingUnit {
    /** Volume in Onuces */
    OZ = "OZ",
    /** Time in minutes */
    TIME = "TIME",
    /** Percentage */
    SALT_PERCENT = "SALT_PERCENT",
};

export class ChemicalDosingProcedureCommon {
    DosingRows:ChemicalDosingItem[];
    PumpMinimumPowerLevelWhileDosing: number;
    PumpMinimumPowerLevelWhileMixing: number;
    ProbeDelayMinutes: number;
}

export class ChemicalDosingProcedure extends ChemicalDosingProcedureCommon {
    ChemicalDosingUnit:ChemicalDosingUnit;
    ErrorMethod:ChemicalDosingErrorConfig;
    MaximumDosingAmountPerDay:number;
}
    
export class ChemicalDosingProcedureSalt extends ChemicalDosingProcedureCommon {
    DosingRowsUseDefault: boolean;
    DutyCycleMinutes: number;
    MaxSaltChlorinatorPPMDosePerDay: number;
    SaltChlorinatorDispenseMethod: GlobalTypes.SaltChlorineDispenseMethods;
    StandAloneSaltChlorinatorPercent: number;
}

export class SaltDosingProcedure {
    NotCovered: ChemicalDosingProcedureSalt;
    CoveredDosingProcedureEnabled: boolean;
    Covered: ChemicalDosingProcedureSalt;
    SaltChlorinatorSuperChlorinateUntil: null;
}

export type ChemicalDosingErrorMethod = "FAILED_DOSE_RATE"|"OVERFEED";
export type ChemicalDosingResumeMethod = "NEVER"|"DAILY";
export class ChemicalDosingErrorConfig {
    ErrorMethod:ChemicalDosingErrorMethod;
    Numerator:number;
    Denominator:number;
    OverFeedAmount:number;
    OverFeedUnit:ChemicalDosingUnit;
    ResumeMethod:ChemicalDosingResumeMethod;
}

export type ChemicalDosingItem = ChemicalDosingItemBase & ChemicalDosingItemMain & ChemicalDosingItemSalt;

export type ChemicalDosingItemBase = {
    FromValue: number;
    ToValue: number;
    MixMinutes: number;
}

export type ChemicalDosingItemMain = ChemicalDosingItemBase & {
    DosingAmount: number;
}

export type ChemicalDosingItemSalt = ChemicalDosingItemBase & {
    DosingPercent: number;
    DosingMinutes: number;
}

export type ChemistryEdit = {
    Acid: boolean,
    AcidDosingSpeedIncrements: [],
    AcidDosingUnits: ChemicalDosingUnit[],
    AcidOnByDefault: boolean,
    AcidRunForSecondsPerOz: number,
    AlternateSaltChlorineWhenCovered: boolean,
    /** Introduced by AutoFilerVersion 32 */
    AvailableSanitizerTargetTypes: SanitizerTypes[],
    Base: boolean,
    BaseDosingSpeedIncrements: [],
    BaseDosingUnits: ChemicalDosingUnit[],
    BaseOnByDefault: boolean,
    BaseRunForSecondsPerOz: number,
    BodyOfWaterUUID: string,
    CO2: boolean,
    CO2DosingSpeedIncrements: [],
    CO2DosingUnits: ChemicalDosingUnit[],
    CO2OnByDefault: boolean,
    DefaultAcidDosingProcedure: ChemicalDosingProcedure,
    DefaultBaseDosingProcedure: ChemicalDosingProcedure,
    DefaultCO2DosingProcedure: ChemicalDosingProcedure,
    DefaultDutyCycleMinutes: number,
    /** Introduced by AutoFilerVersion 32 */
    DefaultFreeChlorineAlertTooHigh: number,
    /** Introduced by AutoFilerVersion 32 */
    DefaultFreeChlorineAlertTooLow: number,
    /** Introduced by AutoFilerVersion 32 */
    DefaultFreeChlorineTarget: number,
    DefaultLiquidChlorineDosingProcedure: ChemicalDosingProcedure,
    /** Introduced by AutoFilerVersion 31 */
    DefaultORPAlertDelayMinutes: number,
    /** Introduced by AutoFilerVersion 31 */
    DefaultORPAlertEnabled: boolean,
    DefaultORPAlertTooHigh: number,
    DefaultORPAlertTooLow: number,
    DefaultORPTarget: number,
    /** Introduced by AutoFilerVersion 31 */
    DefaultPHAlertDelayMinutes: number,
    /** Introduced by AutoFilerVersion 31 */
    DefaultPHAlertEnabled: boolean,
    DefaultPHAlertTooHigh: number,
    DefaultPHAlertTooLow: number,
    DefaultPHTarget: number,
    /** Introduced by AutoFilerVersion 32 */
    DefaultTotalChlorineAlertTooHigh: number,
    /** Introduced by AutoFilerVersion 32 */
    DefaultTotalChlorineAlertTooLow: number,
    /** Introduced by AutoFilerVersion 32 */
    DefaultTotalChlorineTarget: number,
    /** Introduced by AutoFilerVersion 32 */
    DefaultSanitizerAlertDelayMinutes: number,
    /** Introduced by AutoFilerVersion 32 */
    DefaultSanitizerAlertEnabled: boolean,
    DefaultSaltDosingProcedure: SaltDosingProcedure,
    DutyCycleMinutes: boolean,
    LiquidChlorine: boolean,
    LiquidChlorineDispenseMethods: [],
    LiquidChlorineDosingSpeedIncrements: [],
    LiquidChlorineDosingUnits: ChemicalDosingUnit[],
    LiquidChlorineOnByDefault: boolean,
    LiquidChlorineRunForSecondsPerOz: number
    ORPProbe: boolean,
    Ozone: boolean,
    OzoneOnByDefault: boolean,
    PhProbe: boolean,
    /** Introduced by AutoFilterVersion 32 */
    SanitizerSensor: boolean,
    SaltChlorinatorDispenseMethods: [],
    SaltChlorine: boolean,
    SaltChlorineOnByDefault: boolean,
    SaltChlorineSupportPercent: boolean,
    SaltDosingSpeedIncrements: [],
    SaltDosingUnits: ChemicalDosingUnit[],
    ShowEditingIcon: boolean,
    ShowEditingPhTab: boolean,
    ShowEditingSanitizerTab: boolean,
    UvSanitizer: boolean,
    UvSanitizerOnByDefault: boolean,
}

